import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, input } from '@angular/core'
import { MarkdownDirective } from '../../directives'
import { Icon, IconComponent } from '../icon'

/**
 * Component for displaying contextual information or feedback messages to users, for example
 * disclaimers and form validation errors.
 *
 * Alert content can be rendered as HTML or markdown.
 *
 * @example
 * ``` html
 * <ftr-alert type="info">
 *   Be advised that you are reading an alert right now.
 * </ftr-alert>
 * ```
 */
@Component({
  selector: 'ftr-alert',
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.css',
  imports: [IconComponent, MarkdownDirective, NgTemplateOutlet],
})
export class AlertComponent {
  /** @see {@link AlertType} */
  readonly type = input.required<AlertType>()
  readonly icon = input<Icon | undefined>(undefined)

  readonly label = computed<string>(() => AlertLabelMap[this.type()])

  /** Render content using markdown syntax. @default false */
  readonly renderAsMarkdown = input<boolean>(false)

  /** Whether alert should span the width of its content instead of its container. @default false */
  readonly constrainWidth = input<boolean>(false)

  protected readonly actualIcon = computed<Icon>(() => this.icon() ?? iconMap[this.type()])

  protected readonly classes = computed<Record<string, boolean>>(() => ({
    alert: true,
    [`alert--${this.type()}`]: true,
    'alert--constrain-width': this.constrainWidth(),
    'alert--markdown': this.renderAsMarkdown(),
  }))

  protected readonly role = computed<string>(() => (this.type() === AlertType.Error ? 'alert' : 'status'))
}

export const AlertType = {
  General: 'general',
  Info: 'info',
  Success: 'success',
  Warning: 'warning',
  Error: 'error',
} as const
export type AlertType = (typeof AlertType)[keyof typeof AlertType]

const iconMap: Record<AlertType, Icon> = {
  [AlertType.General]: 'AccountCircle',
  [AlertType.Info]: 'InfoCircleSolid',
  [AlertType.Success]: 'CheckmarkCircle',
  [AlertType.Warning]: 'Warning',
  [AlertType.Error]: 'ErrorCircleSolid',
}

const AlertLabelMap: Record<AlertType, string> = {
  [AlertType.General]: 'General',
  [AlertType.Info]: 'Information',
  [AlertType.Success]: 'Success',
  [AlertType.Warning]: 'Warning',
  [AlertType.Error]: 'Error',
}
